<script>
import WootDateRangePicker from 'dashboard/components/ui/DateRangePicker.vue';
import ReportsFiltersDateRange from './Filters/DateRange.vue';
import ReportsFiltersDateGroupBy from './Filters/DateGroupBy.vue';
import ReportsFiltersAgents from './Filters/Agents.vue';
import ReportsFiltersLabels from './Filters/Labels.vue';
import ReportsFiltersInboxes from './Filters/Inboxes.vue';
import ReportsFiltersTeams from './Filters/Teams.vue';
import ReportsFiltersRatings from './Filters/Ratings.vue';
import V4Button from 'dashboard/components-next/button/Button.vue';
import subDays from 'date-fns/subDays';
import { DATE_RANGE_OPTIONS } from '../constants';
import { getUnixStartOfDay, getUnixEndOfDay } from 'helpers/DateHelper';

export default {
  components: {
    WootDateRangePicker,
    ReportsFiltersDateRange,
    ReportsFiltersDateGroupBy,
    ReportsFiltersAgents,
    ReportsFiltersLabels,
    ReportsFiltersInboxes,
    ReportsFiltersTeams,
    ReportsFiltersRatings,
    V4Button,
  },
  props: {
    showGroupByFilter: {
      type: Boolean,
      default: false,
    },
    showAgentsFilter: {
      type: Boolean,
      default: false,
    },
    showLabelsFilter: {
      type: Boolean,
      default: false,
    },
    showInboxFilter: {
      type: Boolean,
      default: false,
    },
    showRatingFilter: {
      type: Boolean,
      default: false,
    },
    showTeamFilter: {
      type: Boolean,
      default: false,
    },
    showBusinessHoursSwitch: {
      type: Boolean,
      default: true,
    },
    showDownloadButton: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['filterChange', 'download'],
  data() {
    return {
      // default value, need not be translated
      selectedDateRange: DATE_RANGE_OPTIONS.LAST_7_DAYS,
      selectedGroupByFilter: null,
      selectedLabel: null,
      selectedInbox: null,
      selectedTeam: null,
      selectedRating: null,
      selectedAgents: [],
      customDateRange: [new Date(), new Date()],
      businessHoursSelected: { id: false, name: '24x7' },
    };
  },
  computed: {
    isDateRangeSelected() {
      return (
        this.selectedDateRange.id === DATE_RANGE_OPTIONS.CUSTOM_DATE_RANGE.id
      );
    },
    isGroupByPossible() {
      return this.selectedDateRange.id !== DATE_RANGE_OPTIONS.LAST_7_DAYS.id;
    },
    to() {
      if (this.isDateRangeSelected) {
        return getUnixEndOfDay(this.customDateRange[1]);
      }
      return getUnixEndOfDay(new Date());
    },
    from() {
      if (this.isDateRangeSelected) {
        return getUnixStartOfDay(this.customDateRange[0]);
      }

      const { offset } = this.selectedDateRange;
      const fromDate = subDays(new Date(), offset);
      return getUnixStartOfDay(fromDate);
    },
    validGroupOptions() {
      return this.selectedDateRange.groupByOptions;
    },
    validGroupBy() {
      if (!this.selectedGroupByFilter) {
        return this.validGroupOptions[0];
      }

      const validIds = this.validGroupOptions.map(opt => opt.id);
      if (validIds.includes(this.selectedGroupByFilter.id)) {
        return this.selectedGroupByFilter;
      }
      return this.validGroupOptions[0];
    },
    businessHoursOptions() {
      return [
        { id: true, name: this.$t('REPORT.BUSINESS_HOURS') },
        { id: false, name: '24x7' },
      ];
    },
  },
  watch: {
    businessHoursSelected() {
      this.emitChange();
    },
  },
  mounted() {
    this.emitChange();
  },
  methods: {
    emitChange() {
      const {
        from,
        to,
        selectedGroupByFilter: groupBy,
        businessHoursSelected: businessHours,
        selectedAgents,
        selectedLabel,
        selectedInbox,
        selectedTeam,
        selectedRating,
      } = this;
      this.$emit('filterChange', {
        from,
        to,
        groupBy,
        businessHours: businessHours.id,
        selectedAgents,
        selectedLabel,
        selectedInbox,
        selectedTeam,
        selectedRating,
      });
    },
    onDateRangeChange(selectedRange) {
      this.selectedDateRange = selectedRange;
      this.selectedGroupByFilter = this.validGroupBy;
      this.emitChange();
    },
    onCustomDateRangeChange(value) {
      this.customDateRange = value;
      this.selectedGroupByFilter = this.validGroupBy;
      this.emitChange();
    },
    onGroupingChange(payload) {
      this.selectedGroupByFilter = payload;
      this.emitChange();
    },
    handleAgentsFilterSelection(selectedAgents) {
      this.selectedAgents = selectedAgents;
      this.emitChange();
    },
    handleLabelsFilterSelection(selectedLabel) {
      this.selectedLabel = selectedLabel;
      this.emitChange();
    },
    handleInboxFilterSelection(selectedInbox) {
      this.selectedInbox = selectedInbox;
      this.emitChange();
    },
    handleTeamFilterSelection(selectedTeam) {
      this.selectedTeam = selectedTeam;
      this.emitChange();
    },
    handleRatingFilterSelection(selectedRating) {
      this.selectedRating = selectedRating;
      this.emitChange();
    },
    onDownload() {
      this.$emit('download');
    },
  },
};
</script>

<template>
  <div class="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between mt-[26px]">
    <!-- Filters (left) -->
    <div
      class="flex flex-col md:flex-row lg:flex-wrap gap-3"
    >
      <ReportsFiltersAgents
        v-if="showAgentsFilter"
        class="filter-flex-item"
        @agents-filter-selection="handleAgentsFilterSelection"
      />
      <ReportsFiltersLabels
        v-if="showLabelsFilter"
        class="filter-flex-item"
        @labels-filter-selection="handleLabelsFilterSelection"
      />
      <ReportsFiltersTeams
        v-if="showTeamFilter"
        class="filter-flex-item"
        @team-filter-selection="handleTeamFilterSelection"
      />
      <ReportsFiltersInboxes
        v-if="showInboxFilter"
        class="filter-flex-item"
        @inbox-filter-selection="handleInboxFilterSelection"
      />
      <ReportsFiltersRatings
        v-if="showRatingFilter"
        class="filter-flex-item"
        @rating-filter-selection="handleRatingFilterSelection"
      />
    </div>

    <!-- Controls (right) -->
    <div class="flex flex-col gap-3 w-full lg:w-auto lg:shrink-0 relative z-0">
      <div class="flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-end lg:justify-end lg:gap-3">
        <div
          v-if="isDateRangeSelected"
          class="action-flex-item"
        >
          <WootDateRangePicker
            show-range
            class="no-margin auto-width"
            :value="customDateRange"
            :confirm-text="$t('REPORT.CUSTOM_DATE_RANGE.CONFIRM')"
            :placeholder="$t('REPORT.CUSTOM_DATE_RANGE.PLACEHOLDER')"
            @change="onCustomDateRangeChange"
          />
        </div>

        <div class="action-flex-item">
          <ReportsFiltersDateRange @on-range-change="onDateRangeChange" />
        </div>

        <div
          v-if="showGroupByFilter && isGroupByPossible"
          class="action-flex-item"
        >
          <ReportsFiltersDateGroupBy
            :valid-group-options="validGroupOptions"
            :selected-option="selectedGroupByFilter"
            @on-grouping-change="onGroupingChange"
          />
        </div>

        <div
          v-if="showBusinessHoursSwitch"
          class="action-flex-item multiselect-wrap--small"
        >
          <multiselect
            v-model="businessHoursSelected"
            :options="businessHoursOptions"
            track-by="id"
            label="name"
            :placeholder="$t('FORMS.MULTISELECT.SELECT_ONE')"
            :select-label="''"
            deselect-label=""
            selected-label=""
            :searchable="false"
            :allow-empty="false"
            class="!h-10 text-n-gray-5"
          />
        </div>

        <div
          v-if="showDownloadButton"
          class="action-flex-item flex items-stretch"
        >
          <V4Button
            class="!h-10 w-full !bg-[#717BBC] !font-semibold"
            :label="$t('REPORT.DOWNLOAD')"
            icon="i-ph-download-simple-bold"
            trailing-icon="true"
            size="sm"
            @click="onDownload"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.filter-flex-item,
.action-flex-item {
  width: 100%;
}

@media (min-width: 1024px) {
  .filter-flex-item {
    flex: 1 1 240px;
    min-width: 240px;
    max-width: 240px;
  }

  .action-flex-item {
    min-width: 180px; 
    max-width: 180px; 
  }
}

:deep(.multiselect__content-wrapper),
:deep(.v-select-list),
:deep(.dropdown-menu) {
  z-index: 1000 !important;
}

/* existing arrow overrides */
:deep(.multiselect__select::before) {
  display: none !important;
}

:deep(.multiselect__select) {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%239aa4b2' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6,9 12,15 18,9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: 14px;
}

:deep(.multiselect--active .multiselect__select) {
  transform: rotate(180deg);
}

:deep(.multiselect) {
  margin-bottom: 0 !important;
}
</style>